import React from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../../context/DataContext';

const Dashboard = () => {
  const { volunteers, farmers, companies, mappings } = useData();

  // Dashboard cards data
  const cards = [
    {
      title: 'Volunteers',
      count: volunteers.length,
      icon: '👨‍🌾',
      description: 'Field workers collecting data',
      color: 'var(--primary-color)',
      link: '/admin/volunteers'
    },
    {
      title: 'Farmers',
      count: farmers.length,
      icon: '🌾',
      description: 'Registered agricultural producers',
      color: 'var(--secondary-color)',
      link: '/admin/farmer-database'
    },
    {
      title: 'Companies',
      count: companies.length,
      icon: '🏢',
      description: 'Partner organizations',
      color: 'var(--accent-color)',
      link: '/admin/companies'
    },
    {
      title: 'Mappings',
      count: mappings.length,
      icon: '🔗',
      description: 'Farmer-company connections',
      color: 'var(--info-color)',
      link: '/admin/mapped-data'
    }
  ];

  return (
    <div style={styles.container}>
      <div style={styles.dashboardHeader}>
        <div style={styles.titleSection}>
          <h1 style={styles.title}>
            <span style={styles.titleIcon}>🌱</span>
            FarmConnect Dashboard
          </h1>
          <p style={styles.subtitle}>
            Welcome to the FarmConnect Portal - Connecting farmers with resources and opportunities
          </p>
        </div>
        <div style={styles.dateSection}>
          <div style={styles.date}>{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
        </div>
      </div>

      <div style={styles.statsOverview}>
        <div style={styles.statsSummary}>
          <h2 style={styles.statsTitle}>Overview</h2>
          <p style={styles.statsDescription}>
            Current statistics across the platform
          </p>
        </div>
        <div style={styles.cardContainer}>
          {cards.map((card, index) => (
            <Link to={card.link} key={index} style={{ textDecoration: 'none' }}>
              <div style={styles.card} className="card">
                <div style={{...styles.cardIconContainer, backgroundColor: card.color}}>
                  <div style={styles.cardIcon}>{card.icon}</div>
                </div>
                <div style={styles.cardContent}>
                  <h2 style={styles.cardTitle}>{card.title}</h2>
                  <p style={styles.cardCount}>{card.count}</p>
                  <p style={styles.cardDescription}>{card.description}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div style={styles.contentRow}>


        <div style={styles.quickActions}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>
              <span style={styles.sectionIcon}>⚡</span>
              Quick Actions
            </h2>
          </div>
          <div style={styles.actionButtons}>
            <Link to="/admin/volunteers" style={styles.actionButton}>
              <span style={styles.actionIcon}>👨‍🌾</span>
              <span style={styles.actionText}>Manage Volunteers</span>
            </Link>
            <Link to="/admin/farmer-database" style={styles.actionButton}>
              <span style={styles.actionIcon}>🌾</span>
              <span style={styles.actionText}>Manage Farmers</span>
            </Link>
            <Link to="/admin/companies" style={styles.actionButton}>
              <span style={styles.actionIcon}>🏢</span>
              <span style={styles.actionText}>Manage Companies</span>
            </Link>
            <Link to="/admin/mapping" style={styles.actionButton}>
              <span style={styles.actionIcon}>🔗</span>
              <span style={styles.actionText}>Create Mapping</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  dashboardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '2rem',
    flexWrap: 'wrap',
  },
  titleSection: {
    flex: '1 1 auto',
  },
  title: {
    margin: 0,
    color: 'var(--text-color)',
    fontSize: '2.2rem',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
  },
  titleIcon: {
    marginRight: '0.75rem',
    fontSize: '2rem',
  },
  subtitle: {
    margin: '0.5rem 0 0 0',
    color: 'var(--text-light)',
    fontSize: '1.1rem',
  },
  dateSection: {
    backgroundColor: 'white',
    padding: '0.75rem 1.25rem',
    borderRadius: '8px',
    boxShadow: 'var(--card-shadow)',
    marginTop: '0.5rem',
  },
  date: {
    color: 'var(--primary-color)',
    fontWeight: '500',
  },
  statsOverview: {
    marginBottom: '2rem',
  },
  statsSummary: {
    marginBottom: '1rem',
  },
  statsTitle: {
    margin: 0,
    color: 'var(--text-color)',
    fontSize: '1.5rem',
  },
  statsDescription: {
    margin: '0.25rem 0 0 0',
    color: 'var(--text-light)',
  },
  cardContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))',
    gap: '1.5rem',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: 'var(--card-shadow)',
    transition: 'transform 0.3s, box-shadow 0.3s',
    height: '100%',
  },
  cardIconContainer: {
    padding: '1.5rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardIcon: {
    fontSize: '2.5rem',
    color: 'white',
  },
  cardContent: {
    padding: '1.5rem',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  cardTitle: {
    margin: '0 0 0.5rem 0',
    fontSize: '1.2rem',
    color: 'var(--text-color)',
  },
  cardCount: {
    margin: '0 0 0.75rem 0',
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: 'var(--primary-color)',
  },
  cardDescription: {
    margin: 0,
    fontSize: '0.9rem',
    color: 'var(--text-light)',
  },
  contentRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '1.5rem',
    marginBottom: '2rem',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.25rem',
    borderBottom: '1px solid var(--border-color)',
    paddingBottom: '0.75rem',
  },
  sectionTitle: {
    margin: 0,
    fontSize: '1.3rem',
    color: 'var(--text-color)',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
  },
  sectionIcon: {
    marginRight: '0.5rem',
  },
  viewAllLink: {
    color: 'var(--primary-color)',
    textDecoration: 'none',
    fontSize: '0.9rem',
    fontWeight: '500',
  },
  recentActivity: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: 'var(--card-shadow)',
  },
  activityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  activityItem: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '1rem',
    backgroundColor: 'var(--background-color)',
    borderRadius: '8px',
    transition: 'transform 0.2s',
  },
  activityIconContainer: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: 'var(--primary-light)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: '1rem',
    flexShrink: 0,
  },
  activityIcon: {
    fontSize: '1.25rem',
  },
  activityContent: {
    flex: 1,
  },
  activityText: {
    margin: 0,
    fontWeight: '500',
    color: 'var(--text-color)',
  },
  activityTime: {
    margin: '0.25rem 0 0 0',
    color: 'var(--text-light)',
    fontSize: '0.85rem',
  },
  quickActions: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: 'var(--card-shadow)',
  },
  actionButtons: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '1rem',
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '1rem',
    backgroundColor: 'var(--background-color)',
    color: 'var(--text-color)',
    borderRadius: '8px',
    textDecoration: 'none',
    fontWeight: '500',
    transition: 'all 0.2s',
    border: '1px solid var(--border-color)',
  },
  actionIcon: {
    fontSize: '1.5rem',
    marginRight: '1rem',
    width: '24px',
    textAlign: 'center',
  },
  actionText: {
    flex: 1,
  },
  infoSection: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '1.5rem',
    marginBottom: '2rem',
  },
  infoCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: 'var(--card-shadow)',
    display: 'flex',
    alignItems: 'flex-start',
  },
  infoIcon: {
    fontSize: '2rem',
    marginRight: '1rem',
    color: 'var(--primary-color)',
  },
  infoContent: {
    flex: 1,
  },
  infoTitle: {
    margin: '0 0 0.5rem 0',
    fontSize: '1.1rem',
    color: 'var(--text-color)',
  },
  infoText: {
    margin: 0,
    color: 'var(--text-light)',
    fontSize: '0.95rem',
  },
};

export default Dashboard;
